import React from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart,
  Area,
  TooltipProps,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { useSearchParams } from 'react-router-dom';
import styles from './tooltip.module.scss';

const getCustomTicks = (
  data: any[],
  xDataKey: string,
  xTickStepHours?: number,
) => {
  if (xTickStepHours) {
    return data
      .filter((item: any) => {
        const hour = Number.parseInt(String(item?.name || '').split(':')[0], 10);
        if (!Number.isFinite(hour)) {
          return false;
        }
        return (hour - 6) % xTickStepHours === 0;
      })
      .map((item: any) => item?.[xDataKey])
      .filter(Boolean);
  }

  const halfIndex = Math.floor(data.length / 2);
  const hasDuplicatedHalf = data.length >= 10
    && data[0]?.name === data[halfIndex]?.name
    && data[halfIndex - 1]?.name === data[data.length - 1]?.name;

  if (hasDuplicatedHalf) {
    const firstHalf = data.slice(0, halfIndex);
    const secondHalf = data.slice(halfIndex);
    const firstMid = firstHalf[Math.floor((firstHalf.length - 1) / 2)]?.name;
    const secondMid = secondHalf[Math.floor((secondHalf.length - 1) / 2)]?.name;

    return [
      firstHalf[0]?.name,
      firstMid,
      firstHalf[firstHalf.length - 1]?.name,
      secondHalf[0]?.name,
      secondMid,
      secondHalf[secondHalf.length - 1]?.name,
    ].filter(Boolean);
  }

  if (data.length <= 4) {
    return data.map((item: any) => item.name);
  }

  const isSalonWeekSeries = xDataKey === 'name'
    && data.length <= 10
    && data.every((item: any) => /^S-?\d+$/.test(String(item?.name ?? '')));
  if (isSalonWeekSeries) {
    return data.map((item: any) => item.name);
  }

  const step = Math.floor((data.length - 1) / 3);

  return [
    data[0].name,
    data[Math.floor(step * 1.5)].name,
    data[Math.floor(step * 2.5)].name,
    data[data.length - 1].name,
  ];
};

const getYAxisTicks = (data: any[], keys: any[]) => {
  const maxValue = data.reduce((acc: number, item: any) => {
    const rowMax = keys.reduce((innerAcc: number, key: any) => {
      const dataKey = key.dataKey || key.name;
      const value = Number(item?.[dataKey] ?? 0);
      return Math.max(innerAcc, Number.isFinite(value) ? value : 0);
    }, 0);
    return Math.max(acc, rowMax);
  }, 0);

  const safeMax = Math.max(1, maxValue);
  const step = Math.ceil(safeMax / 3);
  return [0, step, step * 2, step * 3];
};

type ChartTooltipMode = 'default' | 'weeklyComparison';

type CustomTooltipProps = TooltipProps<ValueType, NameType> & {
  mode: ChartTooltipMode,
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  mode,
}) => {
  const [searchParams] = useSearchParams();
  const magasin = searchParams.get('view') === 'magasin';
  if (active && payload && payload.length) {
    if (mode === 'weeklyComparison') {
      const current = payload.find((p: any) => p.name === '2026');
      const previous = payload.find((p: any) => p.name === '2025');
      const pointData = payload?.[0]?.payload || {};
      const evolutionPct = pointData?.evolutionPct ?? 0;
      const evolutionClass = evolutionPct >= 0 ? styles.positive : styles.negative;
      const evolutionSign = evolutionPct > 0 ? '+' : '';
      return (
        <div className={styles.tooltip}>
          <p className={styles.label}>{label}</p>
          <div className={styles.values}>
            <div className={styles.value}>
              <p style={{ color: current?.color }} className={styles.number}>
                {current?.value ?? 0}
              </p>
              <p>inscriptions 2026</p>
            </div>
            <div className={styles.value}>
              <p className={`${styles.number} ${evolutionClass}`}>
                {evolutionSign}
                {evolutionPct}
                %
              </p>
              <p>vs 2025 ({previous?.value ?? 0})</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.tooltip} >
        <p className={styles.label}>{label}</p>
        {/* <p>Value: {payload[0].value}</p> */}
        <div className={styles.values}>
          {payload.map((p: any) => {
            let tooltipNumberColor = p.color;
            if (p.name === 'J1') tooltipNumberColor = '#2F8FB2';
            if (p.name === 'J2') tooltipNumberColor = '#6A63C8';

            return (
              <div
                key={p.name}
                className={`${styles.value} ${p.name !== 'inscriptions' ? styles.line : ''}`}
              >
                <p style={{ color: tooltipNumberColor }} className={styles.number}>{p.value}</p>
            {p.name === 'inscriptions' && <p>{magasin ? 'magasins' : 'utilisateurs'} inscrits</p>}
            {(p.name === 'J2' || p.name === 'J1') && <p>entrées {p.name}</p>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
};

export default function CustomAreaChart({
  data,
  keys,
  tooltipMode = 'default',
  xDataKey = 'name',
  xTickStepHours,
}: {
  data: any,
  keys: any,
  tooltipMode?: ChartTooltipMode,
  xDataKey?: string,
  xTickStepHours?: number,
}) {
  const yAxisTicks = getYAxisTicks(data, keys);

  const formatXAxisTick = (value: string | number) => {
    const raw = String(value);
    if (raw === 'S0' || raw === 'S-0' || raw === '0') return 'Salon';
    if (/^S-\d+$/.test(raw)) return raw;
    const asNumber = Number(raw);
    if (Number.isFinite(asNumber)) {
      if (asNumber === 0) return 'Salon';
      if (asNumber < 0) return `S${asNumber}`;
      return `S-${asNumber}`;
    }
    return raw;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{
          top: 20,
          right: 20,
          left: 8,
          bottom: 0,
        }}
      >
        <CartesianGrid vertical={false} fillOpacity={0.6} />
        <defs>
        {keys.map((k:any) => <linearGradient key={k.name} id={k.id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={k.color} stopOpacity={0.8}/>
            <stop offset="95%" stopColor={k.color} stopOpacity={0}/>
          </linearGradient>)}
        </defs>
        <XAxis
          dataKey={xDataKey}
          tickLine={false}
          axisLine={false}
          ticks={getCustomTicks(data, xDataKey, xTickStepHours)}
          tickFormatter={formatXAxisTick}
          tick={{ fontSize: 12, fill: '#A4B7BB' }}
        />
        <YAxis
          allowDecimals={false}
          ticks={yAxisTicks}
          tickLine={false}
          axisLine={false}
          domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
          tick={{ fontSize: 12, fill: '#A4B7BB' }}
          />
        <Tooltip content={<CustomTooltip mode={tooltipMode} />} />
        {keys.map((k:any) => <Area
          key={k.name}
          name={k.name}
          type="monotone"
          dataKey={k.dataKey || k.name}
          stroke={k.color}
          fill={k.withFill === false ? 'none' : `url(#${k.id})`}
          strokeDasharray={k.strokeDasharray || undefined}
          dot={k.dot ?? false}
        />)}
      </AreaChart>
    </ResponsiveContainer>
  );
}
